import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import { Button } from '../ui/button';

const POLL_MS = 60_000;
/** Fired by the security page after an acknowledgement or integrity check. */
export const SECURITY_CHANGED_EVENT = 'pd:security-changed';

/**
 * Shown at the top of every admin page while any critical security event
 * is unacknowledged, or the audit log's last integrity check failed. It is
 * deliberately impossible to dismiss without acknowledging the events.
 */
const SecurityAlertBanner = () => {
  const [summary, setSummary] = useState(null);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      adminApi
        .securitySummary()
        .then((r) => { if (!cancelled) setSummary(r.data); })
        .catch(() => {});
    load();
    const t = setInterval(load, POLL_MS);
    window.addEventListener(SECURITY_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener(SECURITY_CHANGED_EVENT, load);
    };
  }, [location.pathname]);

  if (!summary) return null;
  const open = summary.unacknowledged_critical_count || 0;
  const chainProblem = summary.chain?.last_problem;
  if (!open && !chainProblem) return null;
  const onSecurityPage = location.pathname.startsWith('/admin/security');

  return (
    <div
      role="alert"
      className="mb-6 rounded-lg bg-breach-wash px-5 py-4 flex items-start justify-between gap-4 flex-wrap"
      data-testid="security-alert-banner"
    >
      <div className="text-breach-ink">
        <p className="font-semibold">
          {open > 0
            ? `${open} critical security ${open === 1 ? 'event needs' : 'events need'} reviewing.`
            : 'The audit log failed its last integrity check.'}
        </p>
        <p className="text-sm mt-0.5">
          {chainProblem && open > 0 ? 'The audit log also failed its last integrity check. ' : ''}
          Every admin has been emailed. This stays here until each one is acknowledged.
        </p>
      </div>
      {!onSecurityPage && (
        <Button asChild variant="breach">
          <Link to="/admin/security" data-testid="security-alert-banner-link">
            Review security events
          </Link>
        </Button>
      )}
    </div>
  );
};

export default SecurityAlertBanner;
